import React, { useState, useEffect, useCallback } from "react";
import { Box, Container, Typography } from "@mui/material";
import { apiClient } from "../api/httpClient";
import Navbar from "../globals/components/Navbar";
import useApproveBonds, { bondTabs } from "./hooks/useApproveBonds";
import ApproveBondsStatsDialog from "./components/ApproveBondsStatsDialog";
import ApproveBondsHeader from "./components/ApproveBondsHeader";
import ApproveBondsBondTypeToggle from "./components/ApproveBondsBondTypeToggle";
import ApproveBondsTabsSearchToolbar from "./components/ApproveBondsTabsSearchToolbar";
import ApproveBondsDataCorrectionsPanel from "./components/ApproveBondsDataCorrectionsPanel";
import ApproveBondsMainBondsPanel from "./components/ApproveBondsMainBondsPanel";
import ApproveBondsLoadMoreSection from "./components/ApproveBondsLoadMoreSection";
import ApproveBondsEndorsementDialog from "./components/ApproveBondsEndorsementDialog";
import ApproveBondsCancellationDialog from "./components/ApproveBondsCancellationDialog";
import ApproveBondsDataCorrectionDetailDialog from "./components/ApproveBondsDataCorrectionDetailDialog";

function ApproveBondsPage() {
  const [docGenStatuses, setDocGenStatuses] = useState([]);

  const {
    selectedType,
    tabIndex,
    loading,
    isCancelling,
    setIsCancelling,
    searchQuery,
    setSearchQuery,
    page,
    rowsPerPage,
    toBeCancelledBonds,
    statusCounts,
    activeCounts,
    start,
    end,
    hasMore,
    cancelDialogOpen,
    setCancelDialogOpen,
    endorsementDialogOpen,
    setEndorsementDialogOpen,
    selectedBondToEndorse,
    endorsementType,
    setEndorsementType,
    newBondAmount,
    setNewBondAmount,
    endorsementFile,
    setEndorsementFile,
    isSubmittingEndorsement,
    setIsSubmittingEndorsement,
    role,
    contactName,
    setContactName,
    contactEmail,
    setContactEmail,
    updatedName,
    setUpdatedName,
    counties,
    endorsementToCounty,
    setEndorsementToCounty,
    loadingCounties,
    estimatedAssets,
    setEstimatedAssets,
    dwbFirstName,
    setDwbFirstName,
    dwbMiddleName,
    setDwbMiddleName,
    dwbLastName,
    setDwbLastName,
    dwbSuffix,
    setDwbSuffix,
    dwbAlias,
    setDwbAlias,
    endorsementPrincipalId,
    setEndorsementPrincipalId,
    selectedBondToCancel,
    setSelectedBondToCancel,
    cancellationFile,
    setCancellationFile,
    isMarkingCancellationComplete,
    dataCorrectionsList,
    dataCorrectionsLoading,
    selectedDataCorrection,
    setSelectedDataCorrection,
    dataCorrectionDetailOpen,
    setDataCorrectionDetailOpen,
    completingCorrectionId,
    stats,
    dataLoading,
    statsOpen,
    setStatsOpen,
    refreshData,
    getCorrectionId,
    updateCorrectionStatusToComplete,
    fetchBonds,
    formatBondAmount,
    filteredData,
    currentRows,
    handleBondTypeClick,
    handleTabChange,
    handleChangePage,
    handleChangeRowsPerPage,
    formatTime,
    getEndorsementInstructions,
    getCancellationInstructionsList,
    handleMarkCancellationComplete,
    handleRevertCancellation,
    setSelectedBondToEndorse,
  } = useApproveBonds();

  const fetchDocGenStatus = useCallback(async () => {
    try {
      const res = await apiClient("/doc-generation-status/", "GET");
      setDocGenStatuses(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error("Failed to fetch doc generation status:", e);
    }
  }, []);

  useEffect(() => {
    fetchDocGenStatus();
    const intervalMs = statsOpen ? 4000 : 10000;
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchDocGenStatus();
      }
    }, intervalMs);
    return () => clearInterval(interval);
  }, [fetchDocGenStatus, statsOpen]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchDocGenStatus();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchDocGenStatus]);

  return (
    <>
      <Navbar />
      <Container
        maxWidth={false}
        disableGutters
        sx={{ py: 3, px: { xs: 2, sm: 3 } }}
      >
        <ApproveBondsHeader
          setStatsOpen={setStatsOpen}
          refreshData={refreshData}
          loading={loading}
        />

        <ApproveBondsStatsDialog
          statsOpen={statsOpen}
          setStatsOpen={setStatsOpen}
          dataLoading={dataLoading}
          stats={stats}
          formatTime={formatTime}
          docGenStatuses={docGenStatuses}
        />

        <ApproveBondsBondTypeToggle
          selectedType={selectedType}
          handleBondTypeClick={handleBondTypeClick}
          toBeCancelledBonds={toBeCancelledBonds}
          activeCounts={activeCounts}
        />

        {selectedType && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              {selectedType} Bonds
            </Typography>

            <ApproveBondsTabsSearchToolbar
              tabIndex={tabIndex}
              handleTabChange={handleTabChange}
              statusCounts={statusCounts}
              selectedType={selectedType}
              dataCorrectionsList={dataCorrectionsList}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />

            {bondTabs[tabIndex] === "Data Corrections" ? (
              <ApproveBondsDataCorrectionsPanel
                dataCorrectionsLoading={dataCorrectionsLoading}
                searchQuery={searchQuery}
                selectedType={selectedType}
                dataCorrectionsList={dataCorrectionsList}
                setSelectedDataCorrection={setSelectedDataCorrection}
                setDataCorrectionDetailOpen={setDataCorrectionDetailOpen}
                completingCorrectionId={completingCorrectionId}
                getCorrectionId={getCorrectionId}
                updateCorrectionStatusToComplete={updateCorrectionStatusToComplete}
              />
            ) : (
              <ApproveBondsMainBondsPanel
                tabIndex={tabIndex}
                loading={loading}
                filteredData={filteredData}
                currentRows={currentRows}
                formatBondAmount={formatBondAmount}
                refreshData={refreshData}
                page={page}
                handleChangePage={handleChangePage}
                rowsPerPage={rowsPerPage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                setSelectedBondToCancel={setSelectedBondToCancel}
                setCancellationFile={setCancellationFile}
                setCancelDialogOpen={setCancelDialogOpen}
                setSelectedBondToEndorse={setSelectedBondToEndorse}
                setEndorsementType={setEndorsementType}
                setNewBondAmount={setNewBondAmount}
                setEndorsementFile={setEndorsementFile}
                setUpdatedName={setUpdatedName}
                setEndorsementToCounty={setEndorsementToCounty}
                setEstimatedAssets={setEstimatedAssets}
                setDwbFirstName={setDwbFirstName}
                setDwbMiddleName={setDwbMiddleName}
                setDwbLastName={setDwbLastName}
                setDwbSuffix={setDwbSuffix}
                setDwbAlias={setDwbAlias}
                setEndorsementDialogOpen={setEndorsementDialogOpen}
              />
            )}
          </Box>
        )}

        <ApproveBondsLoadMoreSection
          hasMore={hasMore}
          tabIndex={tabIndex}
          fetchBonds={fetchBonds}
          start={start}
          end={end}
        />
      </Container>

      <ApproveBondsEndorsementDialog
        endorsementDialogOpen={endorsementDialogOpen}
        setEndorsementDialogOpen={setEndorsementDialogOpen}
        isSubmittingEndorsement={isSubmittingEndorsement}
        setIsSubmittingEndorsement={setIsSubmittingEndorsement}
        selectedBondToEndorse={selectedBondToEndorse}
        endorsementType={endorsementType}
        setEndorsementType={setEndorsementType}
        counties={counties}
        loadingCounties={loadingCounties}
        endorsementToCounty={endorsementToCounty}
        setEndorsementToCounty={setEndorsementToCounty}
        updatedName={updatedName}
        setUpdatedName={setUpdatedName}
        dwbFirstName={dwbFirstName}
        setDwbFirstName={setDwbFirstName}
        dwbMiddleName={dwbMiddleName}
        setDwbMiddleName={setDwbMiddleName}
        dwbLastName={dwbLastName}
        setDwbLastName={setDwbLastName}
        dwbSuffix={dwbSuffix}
        setDwbSuffix={setDwbSuffix}
        dwbAlias={dwbAlias}
        setDwbAlias={setDwbAlias}
        endorsementPrincipalId={endorsementPrincipalId}
        setEndorsementPrincipalId={setEndorsementPrincipalId}
        newBondAmount={newBondAmount}
        setNewBondAmount={setNewBondAmount}
        estimatedAssets={estimatedAssets}
        setEstimatedAssets={setEstimatedAssets}
        endorsementFile={endorsementFile}
        setEndorsementFile={setEndorsementFile}
        contactName={contactName}
        setContactName={setContactName}
        contactEmail={contactEmail}
        setContactEmail={setContactEmail}
        role={role}
        getEndorsementInstructions={getEndorsementInstructions}
        refreshData={refreshData}
      />

      <ApproveBondsCancellationDialog
        cancelDialogOpen={cancelDialogOpen}
        setCancelDialogOpen={setCancelDialogOpen}
        isCancelling={isCancelling}
        setIsCancelling={setIsCancelling}
        selectedBondToCancel={selectedBondToCancel}
        cancellationFile={cancellationFile}
        setCancellationFile={setCancellationFile}
        getCancellationInstructionsList={getCancellationInstructionsList}
        handleMarkCancellationComplete={handleMarkCancellationComplete}
        handleRevertCancellation={handleRevertCancellation}
        isMarkingCancellationComplete={isMarkingCancellationComplete}
        refreshData={refreshData}
      />

      <ApproveBondsDataCorrectionDetailDialog
        dataCorrectionDetailOpen={dataCorrectionDetailOpen}
        setDataCorrectionDetailOpen={setDataCorrectionDetailOpen}
        selectedDataCorrection={selectedDataCorrection}
        setSelectedDataCorrection={setSelectedDataCorrection}
        completingCorrectionId={completingCorrectionId}
        getCorrectionId={getCorrectionId}
        updateCorrectionStatusToComplete={updateCorrectionStatusToComplete}
      />
    </>
  );
}

export default ApproveBondsPage;
